import React from "react"
import { useTranslation } from "react-i18next"
import { Select, SelectContent, SelectItem, SelectGroup } from "@/components/ui/Select"
import { SelectTrigger } from "@radix-ui/react-select"
import clsx from "clsx"
import { ThumbnailSize } from "./Thumbnail"
import { PhotoRecord } from "@/lib/catalog"
import { setColorTag, setColorTagForPhoto } from "@/store/photo/store-photos"
import { ColorTag, ColorTagValues } from "@/utils/exif"
import { colorTagKeys } from "@/i18n/dual-role-strings"

// i18n-ignore: ColorTag values are dual-role data keys, translated at render boundary
const COLOR_MAP = {
	"No Color": "border border-base-300 hover:border-base-100", // i18n-ignore: ColorTag enum key
	Red: "bg-red-600 hover:bg-red-500",
	Green: "bg-green-600 hover:bg-green-500",
	Yellow: "bg-yellow-600 hover:bg-yellow-500",
	Blue: "bg-blue-600 hover:bg-blue-500",
	Purple: "bg-purple-600 hover:bg-purple-500",
} as const

const ThumbnailColorSetter: React.FC<{
	size: ThumbnailSize
	photo: PhotoRecord
	isSelected: boolean
	isHovering: boolean
	isMenuOpen: boolean
	shouldShowInlineControls: boolean
	setIsMenuOpen: (open: boolean) => void
}> = ({
	size,
	photo,
	isSelected,
	setIsMenuOpen,
	isHovering,
	isMenuOpen,
	shouldShowInlineControls,
}) => {
	const { t } = useTranslation("components")
	const thSize = size === "small" ? "size-2.5" : "size-3"

	return (
		<Select
			trackingLabel="thumbnail_color_setter"
			aria-label={t("thumbnailColorSetter.ariaSetPhotoColorTag")}
			value={photo.colorTag ?? "No Color"} // i18n-ignore: ColorTag enum value
			onValueChange={(value: ColorTag) => {
				isSelected ? setColorTag(value) : setColorTagForPhoto(photo, value)
			}}
			onOpenChange={(open) => setIsMenuOpen(open)}
		>
			<SelectTrigger>
				<div
					className={clsx(
						isHovering || isMenuOpen || shouldShowInlineControls
							? "visible"
							: "invisible",
						thSize,
						COLOR_MAP[photo.colorTag || "No Color"], // i18n-ignore: ColorTag enum value
						"rounded-full",
					)}
				></div>
			</SelectTrigger>
			<SelectContent side="top" align="center">
				<SelectGroup>
					{ColorTagValues.map((color) => (
						<SelectItem key={color} value={color}>
							<div className="flex flex-row items-center space-x-1">
								<div
									className={clsx(COLOR_MAP[color], "size-3 rounded-full")}
								></div>
								<div className="text-xs">{t(colorTagKeys[color])}</div>
							</div>
						</SelectItem>
					))}
				</SelectGroup>
			</SelectContent>
		</Select>
	)
}

export const MemoizedThumbnailColorSetter = React.memo(ThumbnailColorSetter, (prev, next) => {
	if (prev.photo.id !== next.photo.id) {
		return false
	}

	if (prev.photo.colorTag !== next.photo.colorTag) {
		return false
	}

	if (prev.isHovering !== next.isHovering) {
		return false
	}

	if (prev.isSelected !== next.isSelected) {
		return false
	}

	if (prev.shouldShowInlineControls !== next.shouldShowInlineControls) {
		return false
	}

	if (prev.setIsMenuOpen !== next.setIsMenuOpen) {
		console.error(
			"Potential performance issue in ThumbnailColorSetter, is setIsMenuOpen stable?", // i18n-ignore
		)
		return false
	}

	return true
})
